/**
 * @file mlrRichText.ts
 * @brief Plain-text ↔ HTML helpers for the MLR TipTap editor.
 *
 * @details
 * Existing statements are plain narrative. The editor stores a small HTML
 * subset (`p`, `br`, `strong`, `em`, `u`, lists). PDF export strips markup
 * via BACKEND `stripMlrMarkup` (08-09-2026).
 *
 * @author DES
 * @date 08-09-2026
 */

export function looksLikeHtml(value: string): boolean {
  // `b`/`i` included — execCommand emits them, and without them inline-only
  // markup was treated as plain text and survived into the PDF. Keep in step
  // with BACKEND export/stripMlrMarkup.mjs. (13-09-2026)
  return /<\/?(p|br|strong|b|em|i|u|ul|ol|li|div|span)\b/i.test(String(value || ""));
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Convert stored plain text (or HTML) into TipTap document HTML. */
export function toEditorHtml(value: string): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "<p></p>";
  if (looksLikeHtml(raw)) return raw;
  const blocks = raw.split(/\n{2,}/);
  const html = blocks
    .map((block) => {
      const lines = block.split(/\n/).map((line) => escapeHtml(line));
      return `<p>${lines.join("<br>")}</p>`;
    })
    .join("");
  return html || "<p></p>";
}

export function htmlToPlain(value: string): string {
  let text = String(value ?? "");
  if (!looksLikeHtml(text)) return text;
  text = text.replace(/<\/p>\s*<p>/gi, "\n\n");
  text = text.replace(/<br\s*\/?>/gi, "\n");
  text = text.replace(/<li[^>]*>/gi, "• ");
  text = text.replace(/<\/(li|ul|ol|p|div|h[1-6])>/gi, "\n");
  text = text.replace(/<[^>]+>/g, "");
  text = text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"');
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

export function normalizeRichHtml(value: string): string {
  return toEditorHtml(value)
    .replace(/<p>\s*<\/p>/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function richTextEqual(a: string, b: string): boolean {
  return normalizeRichHtml(a) === normalizeRichHtml(b);
}

export function isRichTextEmpty(value: string): boolean {
  return htmlToPlain(value).trim().length === 0;
}
